import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

export default function GuestBookingView() {
  const { hash } = useParams<{ hash: string }>();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const { data, isLoading, error, isError } = useQuery({
    queryKey: ["guest-booking", hash],
    queryFn: async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/guest/${hash}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to fetch booking");
      }
      return res.json();
    },
    enabled: !!hash,
  });

  const requestMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/guest/${hash}/request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, options: selectedOptions }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send request");
      }
      return res.json();
    },
    onSuccess: () => {
      setMessage("");
      setStatus("success");
      queryClient.invalidateQueries({
        queryKey: ["guest-booking", hash as string],
      });
    },
    onError: () => setStatus("error"),
  });

  const recentRequests = useMemo(() => {
    return (
      data?.guestRequests.filter((req: any) => {
        const created = new Date(req.createdAt).getTime();
        const now = Date.now();
        return now - created <= 24 * 60 * 60 * 1000;
      }) ?? []
    );
  }, [data?.guestRequests]);

  const dailyLimitReached = useMemo(
    () => recentRequests.length >= 3,
    [recentRequests]
  );

  const bookingExpired = useMemo(() => {
    if (!data?.endDate) return false;
    return new Date(data.endDate) < new Date();
  }, [data?.endDate]);

  const requestOptions = ["Cleaning", "Towels / Sheets", "Food", "Drinks"];
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  if (!hash || isError || !data || !data.id) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-red-700 mb-4">
          Invalid Booking Link
        </h1>
        <p className="text-sm text-gray-700 mb-2">
          This link appears to be invalid or expired. Please contact reception
          for assistance.
        </p>
        <p className="text-sm font-semibold text-[#006838]">
          Reception: +385 53 652 589
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#006838]">
          Welcome to your Booking
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          On this page, you can request additional services, such as room
          cleaning or extras during your stay.
        </p>
      </div>

      {isLoading && <p className="text-gray-500">Loading booking...</p>}

      {data && (
        <>
          <div className="mb-6 space-y-2 text-sm">
            <p>
              <strong>Booking reference:</strong> {data.bookingReference}
            </p>
            <p>
              <strong>Name:</strong> {data.name}
            </p>
            <p>
              <strong>Email:</strong> {data.email}
            </p>
            <p>
              <strong>Check-in:</strong>{" "}
              {new Date(data.startDate).toLocaleDateString()}
            </p>
            <p>
              <strong>Check-out:</strong>{" "}
              {new Date(data.endDate).toLocaleDateString()}
            </p>
          </div>

          <h3 className="text-lg font-semibold mb-2">Your Requests</h3>
          {data.guestRequests.length === 0 ? (
            <p className="text-sm text-gray-500 italic mb-4">
              No requests submitted yet.
            </p>
          ) : (
            <ul className="space-y-2 mb-6 text-sm">
              {data.guestRequests.map((req: any) => (
                <li key={req.id} className="border rounded-md p-2 bg-gray-50">
                  <div className="font-medium">{req.message}</div>
                  {req.options?.length > 0 && (
                    <div className="text-xs text-gray-600">
                      Services: {req.options.join(", ")}
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    {new Date(req.createdAt).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {bookingExpired && (
            <p className="text-sm text-red-600 mt-4">
              Your booking has ended. If you need any assistance, please contact
              reception at <strong>+385 53 652 589</strong>.
            </p>
          )}

          {!dailyLimitReached && !bookingExpired && (
            <div>
              <h3 className="text-lg font-semibold mb-2">
                Submit Additional Request
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setStatus("idle");
                  if (message.trim() || selectedOptions.length > 0)
                    requestMutation.mutate();
                }}
                className="space-y-3"
              >
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">I need:</p>
                  <div className="flex flex-wrap gap-3">
                    {requestOptions.map((option) => (
                      <label
                        key={option}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          value={option}
                          checked={selectedOptions.includes(option)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedOptions((prev) =>
                              prev.includes(value)
                                ? prev.filter((v) => v !== value)
                                : [...prev, value]
                            );
                          }}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  placeholder="E.g. Extra towel, late checkout..."
                />
                <button
                  type="submit"
                  disabled={
                    requestMutation.isPending ||
                    (!message.trim() && selectedOptions.length === 0)
                  }
                  className="w-full rounded-md bg-[#006838] px-4 py-2 text-white hover:bg-green-800 transition disabled:opacity-50"
                >
                  {requestMutation.isPending ? "Sending..." : "Send Request"}
                </button>

                {status === "success" && (
                  <p className="text-green-600 text-sm">
                    Request sent successfully.
                  </p>
                )}
                {status === "error" && (
                  <p className="text-red-600 text-sm">
                    {error || "Failed to send request."}
                  </p>
                )}
              </form>
            </div>
          )}

          {dailyLimitReached && (
            <p className="text-red-600 text-sm mt-2">
              You’ve reached the daily limit of 3 requests. Please contact
              reception for further assistance.
            </p>
          )}
        </>
      )}
    </div>
  );
}
